
    import { Component, Inject, Mixins } from 'vue-property-decorator'
    import { mapState } from 'vuex'
    import TopMenu from '@/app/common/components/layout/top-menu.vue'
    import OtpInput from '@/app/common/components/otp-input.vue'
    import LockoutOverlay from '@/app/settings/components/lockout-overlay.vue'
    import ResendCodeLink from '@/app/common/components/resend-code-link.vue'
    import { LoginWithTwoFactorCommand } from '@/app/auth/commands/login-with-twofactor.command'
    import { SendEmailOtpCommand } from '@/app/auth/commands/send-email-otp.command'
    import { UpdateConsumerCommand } from '@/app/auth/commands/update-consumer.command'
    import { NotificationDispatcher } from '@/app/common/services/notification-dispatcher.service'
    import { TwoFactorRateLimitError } from '@/app/common/services/http/axios-client.service'
    import { GetProductDefinitionsCommand } from '@/app/product/commands/get-product-definitions.command'
    import { SetSentryUserCommand } from '@/app/auth/commands/set-sentry-user.command'
    import { GetCurrentLanguageCommand } from '@/app/license/commands/get-current-language.command'
    import { GetCurrentLicenseCommand } from '@/app/license/commands/get-current-license-command'
    import { Consumer } from '@/app/identity'
    import moment from 'moment/moment'
    import {
        twoFactorLoginStorage,
        TwoFactorMethod,
        TwoFactorStorage,
        TWOFACTOR_RATE_LIMITS
    } from '@/app/auth/services/twofactor-storage.service'
    import { AddressStyleTranslationMixin } from '@/app/common/helpers/address-style-translation'
    import { TwoFactorLockoutMixin } from '@/app/common/helpers/twofactor-lockout.mixin'

    @Component({
        components: { TopMenu, OtpInput, LockoutOverlay, ResendCodeLink },
        computed: {
            ...mapState('auth', ['consumer'])
        },
        mixins: [AddressStyleTranslationMixin]
    })
    export default class TwoFactorLogin extends Mixins(TwoFactorLockoutMixin) {
        consumer!: Consumer

        loading = false
        selectedMethod: TwoFactorMethod = 'totp'
        emailSent = false
        maskedEmail = ''
        sendingEmail = false

        // From route query params
        consumerId: string | null = null
        defaultMethod: TwoFactorMethod = 'totp'

        @Inject() loginWithTwoFactorCommand!: LoginWithTwoFactorCommand
        @Inject() sendEmailOtpCommand!: SendEmailOtpCommand
        @Inject() updateConsumerCommand!: UpdateConsumerCommand
        @Inject() notificationDispatcher!: NotificationDispatcher
        @Inject() getProductDefinitionsCommand!: GetProductDefinitionsCommand
        @Inject() setSentryUserCommand!: SetSentryUserCommand
        @Inject() getCurrentLanguageCommand!: GetCurrentLanguageCommand
        @Inject() getCurrentLicenseCommand!: GetCurrentLicenseCommand

        // Override storage getter from mixin
        get lockoutStorage(): TwoFactorStorage {
            return twoFactorLoginStorage
        }

        mounted() {
            // Get state from sessionStorage or query params
            this.restoreState()

            // Restore lockout state if any (from mixin)
            this.restoreLockoutStateWithLogin()

            // If no consumer ID, redirect to login
            if (!this.consumerId) {
                this.$router.push({ name: 'login' })
                return
            }

            // Focus first input for TOTP
            if (this.selectedMethod === 'totp' && !this.isLockedOut && !this.rateLimited) {
                this.$nextTick(() => {
                    this.focusOtpInput()
                })
            }
        }

        restoreState() {
            // First check sessionStorage for existing state
            const storedState = twoFactorLoginStorage.getState()
            if (storedState) {
                this.consumerId = storedState.consumerId
                this.defaultMethod = storedState.method || 'totp'
                this.selectedMethod = storedState.method || 'totp'
                this.maskedEmail = storedState.maskedEmail || ''
                this.emailSent = storedState.emailSent || false

                // Check for lockout seconds directly from a database (passed via login)
                if (storedState.verifyLockoutSeconds && storedState.verifyLockoutSeconds > 0) {
                    this.isLockedOut = true
                    this.lockoutCountdown = storedState.verifyLockoutSeconds
                    // Use constant for duration so progress bar shows correct progress
                    this.lockoutDuration = TWOFACTOR_RATE_LIMITS.VERIFY_LOCKOUT_SECONDS
                }
                if (storedState.emailLockoutSeconds && storedState.emailLockoutSeconds > 0) {
                    this.rateLimited = true
                    this.rateLimitCountdown = storedState.emailLockoutSeconds
                    // Use constant for duration so the progress bar shows correct progress
                    this.rateLimitDuration = TWOFACTOR_RATE_LIMITS.EMAIL_SEND_LOCKOUT_SECONDS
                    this.rateLimitMessage = ''
                }
                return
            }

            // Fall back to query params
            this.consumerId = (this.$route.query.consumerId as string) || null
            this.defaultMethod = (this.$route.query.method as TwoFactorMethod) || 'totp'
            this.selectedMethod = this.defaultMethod
            this.maskedEmail = (this.$route.query.maskedEmail as string) || ''
            this.emailSent = this.$route.query.emailSent === 'true'

            // Save state to sessionStorage
            this.saveState()
        }

        saveState() {
            if (!this.consumerId) return
            twoFactorLoginStorage.saveState({
                consumerId: this.consumerId,
                method: this.selectedMethod,
                maskedEmail: this.maskedEmail,
                emailSent: this.emailSent
            })
        }

        /**
         * Login page has special logic: lockout can come from login response
         * so we only check sessionStorage if not already locked out
         */
        restoreLockoutStateWithLogin() {
            // If lockout was already set from restoreState (from login response), skip
            if (this.isLockedOut || this.rateLimited) {
                return
            }

            // Fall back to mixin's restore method
            this.restoreLockoutState()
        }

        get canSubmit(): boolean {
            if (this.isLockedOut || this.rateLimited) return false
            if (this.selectedMethod === 'email' && !this.emailSent) return false
            return this.otpComplete
        }

        // Override endLockout to add focus behavior
        endLockout() {
            // Reset lockout state and check if we were actually locked out
            if (!this.resetLockoutState()) return

            // For email method, the old code is expired (10 min validity < 15 min lockout)
            // Reset emailSent and automatically request a new code
            if (this.selectedMethod === 'email') {
                this.emailSent = false
                this.resetOtpDigits()
                this.saveState()
                this.sendEmailCode()
                return
            }

            // For TOTP, focus the input
            this.$nextTick(() => {
                this.focusOtpInput()
            })
        }

        onOtpChange(digits: string[]) {
            this.otpDigits = digits
            // Auto-submit when complete
            if (this.otpComplete && !this.loading && !this.isLockedOut) {
                this.submit()
            }
        }

        async sendEmailCode() {
            if (this.sendingEmail) return

            const isResend = this.emailSent

            try {
                this.sendingEmail = true
                const result = await this.sendEmailOtpCommand.run(this.consumerId!)

                if (result.noEmail) {
                    await this.notificationDispatcher.error(
                        result.message || (this.$t('page.twofactor.error.no_email') as string)
                    )
                    return
                }

                this.maskedEmail = result.maskedEmail || ''
                this.emailSent = true
                this.resetOtpDigits()
                this.saveState()

                // Start cooldown timer after successful resend
                if (isResend) {
                    const resendCodeLink = this.$refs.resendCodeLink as ResendCodeLink
                    if (resendCodeLink) {
                        resendCodeLink.startCooldown()
                    }
                }

                // Focus first input
                this.$nextTick(() => {
                    this.focusOtpInput()
                })
            } catch (err) {
                if (err instanceof TwoFactorRateLimitError) {
                    this.setRateLimit(err.retryAfterMinutes, err.message)
                    return
                }
                await this.notificationDispatcher.error((err as Error).message)
            } finally {
                this.sendingEmail = false
            }
        }

        async submit() {
            if (!this.canSubmit || this.loading) return

            try {
                this.loading = true
                this.codeError = ''
                this.codeErrorActive = false

                await this.loginWithTwoFactorCommand.run(this.consumerId!, this.confirmationCode, this.selectedMethod)

                // Success - clear state and redirect
                this.clearState()

                await this.updateConsumerCommand.run()
                await this.setSentryUserCommand.run(this.consumer)
                await this.getProductDefinitionsCommand.run()
                await this.getCurrentLanguage()
                await this.getLicenseData()
                this.$emit('reloadThemeEvent')

                await this.$router.push({ name: 'dashboard' })
                await this.notificationDispatcher.success(this.$t('messages.youreauthenticated') as string)
            } catch (err) {
                // Handle rate limit error from backend
                if (err instanceof TwoFactorRateLimitError) {
                    if (err.errorCode === 'VERIFY_RATE_LIMITED') {
                        // OTP verification lockout - use startLockout for consistency
                        this.lockoutDuration = err.retryAfterSeconds || err.retryAfterMinutes * 60
                        this.startLockout()
                    } else {
                        // Email rate limit - use setRateLimit
                        this.setRateLimit(err.retryAfterMinutes, err.message)
                    }
                    this.resetOtpDigits()
                    return
                }

                // Handle incorrect code using mixin method
                this.handleIncorrectCode()
            } finally {
                this.loading = false
            }
        }

        clearState() {
            twoFactorLoginStorage.clearAll()
        }

        goBack() {
            // Clear state but preserve lockout/rate limit state
            twoFactorLoginStorage.clearStateOnly()
            this.$router.push({ name: 'login' })
        }

        async getCurrentLanguage() {
            const language = await this.getCurrentLanguageCommand.run()
            this.$i18n.locale = language.isoCode
            this.$vuetify.lang.current = language.isoCode
            moment.locale(language.isoCode)
        }

        async getLicenseData() {
            await this.getCurrentLicenseCommand.run()
        }
    }
